import React, { useState, useEffect } from 'react';
import { X, Check, Loader2 } from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showError, showSuccess } from '../../utils/toast';
import { jwt_decode } from '../../utils/jwt_decode';
import { useClientLevel } from '../../hooks/useClientLevel';

interface AddEditSwitchGroupModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    mode: 'add' | 'edit';
    group?: { id: number; name: string; description: string } | null;
}

export default function AddEditSwitchGroupModal({
    open,
    onClose,
    onSuccess,
    mode,
    group
}: AddEditSwitchGroupModalProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);

    const level = useClientLevel();
    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;

    useEffect(() => {
        if (open) {
            if (mode === 'edit' && group) {
                setName(group.name);
                setDescription(group.description || '');
            } else {
                setName('');
                setDescription('');
            }
        }
    }, [open, mode, group]);

    if (!open) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim()) {
            showError('Name is required');
            return;
        }

        setLoading(true);
        try {
            const clientId = level?.id;
            if (!clientId) {
                showError('Client ID not found');
                setLoading(false);
                return;
            }

            const payload = {
                organization_id: Number(organizationId),
                client_id: Number(clientId),
                name: name.trim(),
                description: description.trim()
            };

            const response = mode === 'edit' && group?.id
                ? await baseapi.put(`/switch_groups/${group.id}/`, payload)
                : await baseapi.post('/switch_groups/', payload);

            if (response.data.status) {
                showSuccess(mode === 'edit' ? 'Switch group updated successfully' : 'Switch group created successfully');
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || 'Failed to save switch group');
            }
        } catch (error: any) {
            console.error('Error saving switch group:', error);
            showError(error.response?.data?.message || error.message || 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[10000] p-4">
                <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-2xl rounded-xl text-gray-900 dark:text-white relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
                    
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-transparent">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {mode === 'edit' ? 'Edit Switch Group' : 'Add New Switch Group'}
                        </h2>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 dark:hover:text-white transition-colors p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800">
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                                Group Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
                                placeholder="e.g. Building A - Ground Floor"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                                Description
                            </label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                rows={3}
                                className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400 resize-none"
                                placeholder="Describe the purpose of this switch group..."
                            />
                        </div>

                        {/* Footer Actions */}
                        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-800">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={loading}
                                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-gray-400 dark:hover:border-gray-600 disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg font-semibold hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 min-w-[100px] justify-center"
                            >
                                {loading ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                    <Check className="w-3.5 h-3.5" />
                                )}
                                {mode === 'edit' ? 'Update' : 'Create'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}
